import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Button,
  Typography,
  Divider,
  Paper,
  IconButton,
  Tooltip,
  Tab,
  Tabs
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  ColorLens as ColorLensIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNotification } from '../../contexts/NotificationContext';
import PageHeader from '../../components/common/PageHeader';
import configService from '../../services/configService';
import { useAuth } from '../../hooks/useAuth';
import ImageIcon from '@mui/icons-material/Image';

// Función auxiliar para agrupar configuraciones
const groupConfigurations = (configs) => {
  const groups = {
    'General': [],
    'Citas': [],
    'Correo': [],
    'WhatsApp': [],
    'Interfaz': [],
    'Sistema': []
  };

  configs.forEach(config => {
    if (config.clave.startsWith('email_')) {
      groups['Correo'].push(config);
    } else if (config.clave.startsWith('whatsapp_')) {
      groups['WhatsApp'].push(config);
    } else if (config.clave.includes('cita') || config.clave.includes('dias_') || config.clave.includes('horas_')) {
      groups['Citas'].push(config);
    } else if (config.clave.includes('color') || config.clave.includes('logo') || config.clave.includes('tema')) {
      groups['Interfaz'].push(config);
    } else if (config.clave.includes('version') || config.clave.includes('path') || config.clave.includes('api') || config.clave.includes('url')) {
      groups['Sistema'].push(config);
    } else {
      groups['General'].push(config);
    }
  });

  return groups;
};

// Componente principal
const ConfiguracionGeneral = () => {
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [editMode, setEditMode] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  const isAdmin = user?.rol === 'Administrador';

  // Cargar configuraciones
  useEffect(() => {
    const fetchConfigs = async () => {
      setLoading(true);
      try {
        const response = await configService.getConfigs();
        setConfigs(response.data);
        
        // Inicializa editMode para todas las configuraciones
        const initialEditMode = {};
        response.data.forEach(config => {
          initialEditMode[config.clave] = false;
        });
        setEditMode(initialEditMode);
      } catch (error) {
        console.error('Error fetching configurations:', error);
        showNotification('Error al cargar las configuraciones', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchConfigs();
  }, [refreshKey, showNotification]);

  // Agrupar configuraciones por categoría
  const groupedConfigs = groupConfigurations(configs);
  const tabLabels = Object.keys(groupedConfigs);

  // Manejar cambio de pestaña
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Actualizar una configuración
  const handleUpdateConfig = async (clave, valor) => {
    try {
      await configService.updateConfig(clave, { valor });
      showNotification('Configuración actualizada correctamente', 'success');
      setRefreshKey(old => old + 1); // Refresh list
      setEditMode(prev => ({ ...prev, [clave]: false }));
    } catch (error) {
      console.error('Error updating configuration:', error);
      showNotification('Error al actualizar la configuración', 'error');
    }
  };

  // Renderizar campo de configuración según su tipo
  const renderConfigField = (config) => {
    const isEditing = editMode[config.clave];
    
    // Renderizar según el tipo de configuración
    if (config.tipo === 'boolean') {
      return (
        <TextField
          select
          fullWidth
          label={config.descripcion}
          value={config.valor === 'true' ? 'true' : 'false'}
          disabled={!isEditing}
          onChange={(e) => {
            if (isEditing) {
              handleUpdateConfig(config.clave, e.target.value);
            }
          }}
          SelectProps={{
            native: true,
          }}
        >
          <option value="true">Sí</option>
          <option value="false">No</option>
        </TextField>
      );
    } else if (config.tipo === 'integer') {
      return (
        <TextField
          fullWidth
          type="number"
          label={config.descripcion}
          value={config.valor}
          disabled={!isEditing}
          onChange={(e) => {
            if (isEditing && e.target.value) {
              handleUpdateConfig(config.clave, e.target.value);
            }
          }}
        />
      );
    } else if (config.clave.includes('color')) {
      return (
        <TextField
          fullWidth
          label={config.descripcion}
          value={config.valor}
          disabled={!isEditing}
          onChange={(e) => {
            if (isEditing) {
              handleUpdateConfig(config.clave, e.target.value);
            }
          }}
          InputProps={{
            startAdornment: (
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: config.valor,
                  mr: 1,
                  border: '1px solid #ddd'
                }}
              />
            ),
            endAdornment: isEditing && (
              <Tooltip title="Seleccionar color">
                <IconButton size="small">
                  <ColorLensIcon />
                </IconButton>
              </Tooltip>
            ),
          }}
        />
      );
    } else {
      return (
        <TextField
          fullWidth
          label={config.descripcion}
          value={config.valor}
          disabled={!isEditing}
          type={config.clave.includes('password') ? 'password' : 'text'}
          onChange={(e) => {
            if (isEditing) {
              handleUpdateConfig(config.clave, e.target.value);
            }
          }}
        />
      );
    }
  };

  // Renderizar grupo de configuraciones
  const renderConfigGroup = (groupName) => {
    const configs = groupedConfigs[groupName];
    
    if (!configs || configs.length === 0) {
      return (
        <Box p={3} textAlign="center">
          <Typography variant="body1" color="textSecondary">
            No hay configuraciones en esta categoría.
          </Typography>
        </Box>
      );
    }
    
    return (
      <Grid container spacing={3}>
        {configs.map((config) => (
          <Grid item xs={12} md={6} key={config.clave}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {config.clave}
                </Typography>
                {isAdmin && (
                  <Tooltip title={editMode[config.clave] ? "Guardar" : "Editar"}>
                    <IconButton 
                      size="small" 
                      color={editMode[config.clave] ? "success" : "primary"}
                      onClick={() => {
                        setEditMode(prev => ({
                          ...prev,
                          [config.clave]: !prev[config.clave]
                        }));
                      }}
                    >
                      {editMode[config.clave] ? <SaveIcon /> : <EditIcon />}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {renderConfigField(config)}
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader
        title="Configuración General"
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Configuración', to: '/configuracion' },
          { text: 'General' }
        ]}
      />

      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" my={5}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {tabLabels.map((label, index) => (
                    <Tab key={index} label={label} />
                  ))}
                </Tabs>
              </Box>
              
              {tabLabels.map((label, index) => (
                <Box key={index} hidden={tabValue !== index} sx={{ py: 2 }}>
                  {tabValue === index && renderConfigGroup(label)}
                </Box>
              ))}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ConfiguracionGeneral;